/**
 * JWT Token Handler for Shopify Theme
 * Handles JWT-based authentication without relying on Shopify customer session
 */

class JWTTokenHandler {

    /**
     * Base64 URL decode function
     */
    static base64UrlDecode(str) {
        str += new Array(5 - str.length % 4).join('=');
        str = str.replace(/-/g, '+').replace(/_/g, '/');
        return decodeURIComponent(atob(str).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
    }

    /**
     * Decode JWT token without verification (client-side use only)
     */
    static decodeToken(token) {
        try {
            if (!token || typeof token !== 'string') {
                return null;
            }

            const parts = token.split('.');
            if (parts.length !== 3) {
                return null;
            }

            const payload = this.base64UrlDecode(parts[1]);
            return JSON.parse(payload);
        } catch (error) {
            console.warn('Error decoding JWT token:', error);
            return null;
        }
    }

    /**
     * Check if token is expired
     */
    static isTokenExpired(decodedToken) {
        if (!decodedToken || !decodedToken.exp) {
            return true;
        }

        const currentTime = Math.floor(Date.now() / 1000);
        return decodedToken.exp < currentTime;
    }

    /**
     * Extract customer ID from JWT token
     */
    static getCustomerId(token) {
        const decoded = this.decodeToken(token);
        if (!decoded || this.isTokenExpired(decoded)) {
            return null;
        }
        return decoded.customer_id || null;
    }

    /**
     * Get token expiration date
     */
    static getTokenExpiration(token) {
        const decoded = this.decodeToken(token);
        if (!decoded || !decoded.exp) {
            return null;
        }
        return new Date(decoded.exp * 1000);
    }

    /**
     * Store token in localStorage
     */
    static storeToken(token, key = 'pilgrim_jwt_token') {
        try {
            localStorage.setItem(key, token);
            // Set a flag to indicate user is logged in via JWT
            localStorage.setItem('pilgrim_jwt_logged_in', 'true');
            return true;
        } catch (error) {
            console.warn('Error storing token:', error);
            return false;
        }
    }

    /**
     * Retrieve token from localStorage
     */
    static getStoredToken(key = 'pilgrim_jwt_token') {
        try {
            return localStorage.getItem(key);
        } catch (error) {
            //     newrelic.log('Error retrieving token', {
            //     level: 'error',
            //     customAttributes: {
            //       errMessage: error?.message,
            //       errStack: error?.stack,
            //       kind: error?.name
            //     }
            //   });
            console.warn('Error retrieving token:', error);
            return null;
        }
    }

    /**
     * Remove token from localStorage
     */
    static removeToken(key = 'pilgrim_jwt_token') {
        try {
            localStorage.removeItem(key);
            localStorage.removeItem('pilgrim_jwt_logged_in');
            return true;
        } catch (error) {
            console.warn('Error removing token:', error);
            return false;
        }
    }

    /**
     * Check if user is authenticated
     */
    static isAuthenticated(key = 'pilgrim_jwt_token') {
        const token = this.getStoredToken(key);
        if (!token) {
            return false;
        }

        const decoded = this.decodeToken(token);
        return decoded && !this.isTokenExpired(decoded);
    }
}

// Pilgrim Authentication Manager
const PilgrimAuth = {
    API_BASE_URL: 'https://webengagebackup.discoverpilgrim.com/polls',

    /**
     * Send OTP to mobile number
     */
    sendOTP: function (mobileNumber) {
        return fetch(`${this.API_BASE_URL}/otp_sender/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                mobile_number: mobileNumber
            })
        }).then(response => response.json());
    },

    /**
     * Verify OTP and get JWT token
     */
    verifyOTP: function (mobileNumber, otp) {
        return fetch(`${this.API_BASE_URL}/verify_otp_graphql/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                mobile_number: mobileNumber,
                otp: otp
            })
        }).then(response => response.json());
    },

    /**
     * Register new user
     */
    registerUser: function (userData) {
        return fetch(`${this.API_BASE_URL}/register_user_graphql_new/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(userData)
        }).then(response => response.json());
    },

    /**
     * Verify OTP and get JWT token
     */
    verifyOTPWithRegister: function (mobileNumber, otp) {
        return fetch(`${this.API_BASE_URL}/verify_otp_register_graphql/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                mobile_number: mobileNumber,
                otp: otp
            })
        }).then(response => response.json());
    },

    /**
     * Update customer profile after OTP verification
     */
    updateCustomerProfile: function (profileData) {
        const token = JWTTokenHandler.getStoredToken();
        if (!token) {
            return Promise.reject(new Error('No authentication token found'));
        }

        return fetch(`${this.API_BASE_URL}/update_customer_profile/`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(profileData)
        }).then(response => {
            if (response.status === 401) {
                this.logout();
                throw new Error('Authentication token expired. Please login again.');
            }
            if (!response.ok) {
                return response.json().then(data => {
                    throw new Error(data.message || `Request failed with status ${response.status}`);
                });
            }
            return response.json();
        });
    },

    /**
     * Get customer profile and orders
     */
    getCustomerData: function () {
        const token = JWTTokenHandler.getStoredToken();
        if (!token) {
            return Promise.reject(new Error('No authentication token found'));
        }

        return fetch(`${this.API_BASE_URL}/get_customer_profile_and_orders/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({})
        }).then(response => {
            if (response.status === 401) {
                // Token expired, logout user
                this.logout();
                throw new Error('Authentication token expired. Please login again.');
            }
            if (response.status === 404) {
                this.logout();
                localStorage.removeItem('pilgrim_debug_customer_data');
                emitPgTracking();
                throw new Error('Customer not found. Please register.');
            }
            return response.json();
        });
    },

    /**
     * Handle successful OTP verification
     */
    handleOTPSuccess: function (data) {
        if (data.jwt_token) {
            // Store JWT token
            JWTTokenHandler.storeToken(data.jwt_token);
            console.log('JWT token stored successfully:', data.jwt_token);

            // Immediate debug to check if token is stored
            console.log('Token verification after storage:', {
                stored: JWTTokenHandler.getStoredToken(),
                isLoggedIn: this.isLoggedIn(),
                customerId: this.getCurrentCustomerId()
            });

            // Store customer data for debug (once per session)
            setTimeout(() => {
                console.log('About to store debug data...');
                this.storeCustomerDataForDebug();
            }, 200); // Fast delay for immediate checkout readiness

            // Backup debug storage if first attempt fails
            setTimeout(() => {
                const existingDebugData = localStorage.getItem('pilgrim_debug_customer_data');
                if (!existingDebugData) {
                    console.log('Debug data missing after 1 second - forcing storage');
                    sessionStorage.removeItem('pilgrim_debug_data_stored');
                    this.storeCustomerDataForDebug();
                }
            }, 1000);

            // Show success message in login modal
            const loginContent = document.querySelector('#login-content .login-body');
            if (loginContent) {
                const successBanner = document.createElement('div');
                successBanner.className = 'banner banner--success form__banner';
                successBanner.style.marginBottom = '15px';
                successBanner.innerHTML = `
                    <p class="banner__content">🎉 You're successfully logged in!</p>
                `;

                // Insert the banner at the top of the login content
                loginContent.insertBefore(successBanner, loginContent.firstChild);
            }

            // Trigger login success event
            this.triggerLoginSuccess();
            return true;
        }
        return false;
    },

    /**
     * Check if user is currently logged in
     */
    isLoggedIn: function () {
        return JWTTokenHandler.isAuthenticated();
    },

    /**
     * Get current customer ID
     */
    getCurrentCustomerId: function () {
        const token = JWTTokenHandler.getStoredToken();
        return JWTTokenHandler.getCustomerId(token);
    },

    /**
     * Logout user
     */
    logout: function () {
        JWTTokenHandler.removeToken();
        console.log('User logged out');

        // Redirect to home page
        window.location.href = '/';
    },

    /**
     * Trigger login success event for other scripts to listen
     */
    triggerLoginSuccess: function () {
        // Dispatch custom event
        const event = new CustomEvent('pilgrimLoginSuccess', {
            detail: {
                customerId: this.getCurrentCustomerId(),
                timestamp: new Date()
            }
        });
        document.dispatchEvent(event);

        // Close any open modals
        setTimeout(() => {
            this.closeLoginModals();
        }, 3000);
    },

    /**
     * Initialize authentication check on page load
     */
    init: function () {
        // Check if user is already authenticated
        if (this.isLoggedIn()) {
            console.log('User already authenticated with customer ID:', this.getCurrentCustomerId());

            // Update UI for logged in state
            this.updateUIForLoggedInUser();

            // Debug: Store customer data once per session
            this.storeCustomerDataForDebug();
        }

        // Set up automatic token expiration check
        this.setupTokenExpirationCheck();

        // Set up checkout autofill functionality
        // this.setupCheckoutAutofill();
    },

    /**
     * Update UI elements for logged in user
     */
    updateUIForLoggedInUser: function () {
        // Hide login links and show account links
        const loginLinks = document.querySelectorAll('a[href="/account/login"], a[href*="account/login"]');
        loginLinks.forEach(link => {
            link.href = '/account';
            const text = link.textContent.trim();
            if (text.toLowerCase().includes('login') || text.toLowerCase().includes('sign in')) {
                link.textContent = 'My Account';
            }
        });

        // Update account links to point to our custom account page
        const accountLinks = document.querySelectorAll('a[href="/account"], a[href="/account/"]');
        accountLinks.forEach(link => {
            // We'll handle the account page with our custom implementation
            link.addEventListener('click', this.handleAccountClick.bind(this));
        });
    },

    /**
     * Handle account link clicks
     */
    handleAccountClick: function (event) {
        if (this.isLoggedIn()) {
            // User is logged in via JWT, redirect to our custom account page
            // You can implement a custom account page or load data dynamically
            console.log('Redirecting to JWT-based account page');
        } else {
            // User not logged in, show login modal
            event.preventDefault();
            this.showLoginModal();
        }
    },

    /**
     * Show login modal
     */
    showLoginModal: function () {
        const loginModal = document.getElementById('login-modal');
        if (loginModal) {
            loginModal.style.display = 'block';
            document.body.style.overflow = 'hidden';

            // Remove "open" attribute
            document.getElementById("mobile-menu-drawer").removeAttribute("open");

            // Focus the mobile number input inside login popup
            setTimeout(() => {
                document.querySelector("#loginMobileForm input#mobileNumber").focus();
            }, 500);
        }
    },

    /**
     * Setup automatic token expiration check
     */
    setupTokenExpirationCheck: function () {
        const token = JWTTokenHandler.getStoredToken();
        if (!token) return;

        const expirationDate = JWTTokenHandler.getTokenExpiration(token);
        if (!expirationDate) return;

        const timeUntilExpiration = expirationDate.getTime() - Date.now();

        if (timeUntilExpiration > 0) {
            // Set timeout to automatically logout user when token expires
            setTimeout(() => {
                // alert('Your session has expired. Please login again.');
                this.logout();
            }, timeUntilExpiration);

            console.log(`Token will expire in ${Math.round(timeUntilExpiration / 1000 / 60)} minutes`);
        } else {
            // Token already expired
            console.log('Token has expired, logging out user');
            this.logout();
        }
    },

    /**
 * Store customer data in localStorage for debug purposes (once per session)
 */
    storeCustomerDataForDebug: function () {
        console.log('=== DEBUG DATA STORAGE ATTEMPT ===');
        console.log('Current authentication status:', {
            isLoggedIn: this.isLoggedIn(),
            tokenExists: !!JWTTokenHandler.getStoredToken(),
            customerId: this.getCurrentCustomerId()
        });

        // Check if we've already stored debug data in this session AND it exists in localStorage
        const debugDataStored = sessionStorage.getItem('pilgrim_debug_data_stored');
        const existingDebugData = localStorage.getItem('pilgrim_debug_customer_data');
        console.log('Debug data session flag:', debugDataStored);
        console.log('Existing debug data in localStorage:', !!existingDebugData);

        if (debugDataStored === 'true' && existingDebugData) {
            console.log('Debug data already stored in this session and exists in localStorage - skipping');
            return;
        }

        // If session flag exists but localStorage doesn't, clear the session flag
        if (debugDataStored === 'true' && !existingDebugData) {
            console.log('Session flag exists but localStorage data missing - clearing session flag and proceeding');
            sessionStorage.removeItem('pilgrim_debug_data_stored');
        }

        // Only proceed if user is authenticated
        if (!this.isLoggedIn()) {
            console.log('User not logged in - cannot store debug data');
            return;
        }

        console.log('Starting debug data storage process...');

        // Fetch customer data from API immediately (no delay)
        this.getCustomerData()
            .then(response => {
                console.log('Full API response for debug:', response);

                // Handle both response formats
                const data = response.data || response;
                const customer = data.customer;
                const orders = data.orders;

                if (customer) {
                    emitPgTracking(this.getCurrentCustomerId().split("/").pop());

                    // Prepare debug data object
                    const debugData = {
                        timestamp: new Date().toISOString(),
                        customerId: this.getCurrentCustomerId(),
                        customer: {
                            email: customer.email || 'Not provided',
                            phone: customer.phone || 'Not provided',
                            firstName: customer.firstName || 'Not provided',
                            lastName: customer.lastName || 'Not provided',
                            addresses: []
                        }
                    };

                    // Extract addresses if available
                    if (customer.addresses) {
                        if (customer.addresses.edges) {
                            // GraphQL style response
                            customer.addresses.edges.forEach(addressEdge => {
                                const address = addressEdge.node;
                                debugData.customer.addresses.push({
                                    id: address.id,
                                    firstName: address.firstName,
                                    lastName: address.lastName,
                                    address1: address.address1,
                                    address2: address.address2,
                                    city: address.city,
                                    province: address.province,
                                    zip: address.zip,
                                    country: address.country,
                                    phone: address.phone,
                                    isDefault: address.default
                                });
                            });
                        } else if (Array.isArray(customer.addresses)) {
                            // Direct array response
                            customer.addresses.forEach(address => {
                                debugData.customer.addresses.push({
                                    id: address.id,
                                    firstName: address.firstName,
                                    lastName: address.lastName,
                                    address1: address.address1,
                                    address2: address.address2,
                                    city: address.city,
                                    province: address.province || address.provinceCode,
                                    zip: address.zip,
                                    country: address.country || address.countryCode,
                                    phone: address.phone,
                                    isDefault: address.default
                                });
                            });
                        }
                    }

                    if (orders) {
                        debugData.orders = orders;
                    }

                    // Store in localStorage for debugging
                    try {
                        localStorage.setItem('pilgrim_debug_customer_data', JSON.stringify(debugData));
                        console.log('Debug customer data stored in localStorage:', debugData);
                    } catch (error) {
                        console.warn('Error storing debug data in localStorage:', error);
                    }

                    // Mark that we've stored debug data in this session
                    sessionStorage.setItem('pilgrim_debug_data_stored', 'true');

                } else {
                    console.log('No customer data available for debug storage');
                }
            })
            .catch(error => {
                console.warn('Error fetching customer data for debug:', error);
            });
    },

    /**
    * Get stored debug customer data (helper function for console debugging)
    */
    getDebugCustomerData: function () {
        try {
            const debugData = localStorage.getItem('pilgrim_debug_customer_data');
            if (debugData) {
                const parsedData = JSON.parse(debugData);
                console.log('Debug Customer Data:', parsedData);
                // newrelic.log('Debug Customer Data:', {level: 'debug'})
                return parsedData;
            } else {
                // newrelic.log('No debug customer data found in localStorage', {level: 'debug'})
                console.log('No debug customer data found in localStorage');
                return null;
            }
        } catch (error) {
            console.warn('Error retrieving debug customer data:', error);
            // newrelic.log('Error retrieving debug customer data:', {level: 'error'})
            return null;
        }
    },

    /**
     * Clear debug customer data
     */
    clearDebugData: function () {
        try {
            localStorage.removeItem('pilgrim_debug_customer_data');
            sessionStorage.removeItem('pilgrim_debug_data_stored');
            console.log('Debug customer data cleared');
        } catch (error) {
            console.warn('Error clearing debug data:', error);
        }
    },

    /**
     * Force refresh debug data (manual trigger)
     */
    forceRefreshDebugData: function () {
        console.log('=== FORCE REFRESH DEBUG DATA ===');
        // Clear existing flags
        sessionStorage.removeItem('pilgrim_debug_data_stored');
        localStorage.removeItem('pilgrim_debug_customer_data');

        // If user is logged in, immediately store debug data
        if (this.isLoggedIn()) {
            console.log('User is logged in - forcing debug data storage');
            this.storeCustomerDataForDebug();
        } else {
            console.log('User not logged in - cannot force debug data storage');
        }
    },

    /**
     * Close all login modals
     */
    closeLoginModals: function () {
        // Close login modal
        const loginModal = document.getElementById('login-modal');
        if (loginModal) {
            loginModal.style.display = 'none';
        }

        // Close prelogin modal
        const preloginModal = document.getElementById('prelogin-modal');
        if (preloginModal) {
            preloginModal.style.display = 'none';
        }

        // Restore body scroll
        document.body.style.overflow = '';

        console.log('Login modals closed');
    },

    /**
     * Show prelogin modal
     */
    showPreloginModal: function () {
        const preloginModal = document.getElementById('prelogin-modal');
        if (preloginModal) {
            preloginModal.style.display = 'block';
            document.body.style.overflow = 'hidden';

            // Remove "open" attribute
            document.getElementById("mobile-menu-drawer").removeAttribute("open");

            // Focus the mobile number input inside login popup
            setTimeout(() => {
                document.querySelector("#mobileForm input#custPhone").focus();
            }, 500);
        }
    },

    /**
     * Handle checkout redirecting
     */
    handleCheckoutRedirecting: function () {
        // If user email is available, redirect to checkout page with email
        console.log('Redirecting to checkout page');
        // try {
        //     posthog.capture('checkout_button_clicked', {
        //         page_url: window.location.href,
        //         productIds: cartQuantity.filter(item => item.quantity > 0).map(item => item.product_id),
        //         variantIds: cartQuantity.filter(item => item.quantity > 0).map(item => item.id)
        //     });
        // } catch (error) {
        //     console.warn('Analytics tracking failed:', error);
        // }

        try {
            if (localStorage.getItem("pilgrim_debug_customer_data") && localStorage.getItem("pilgrim_debug_customer_data") != "") {
                const customer_data = JSON.parse(localStorage.getItem("pilgrim_debug_customer_data"));

                const pilgrim_token = localStorage.getItem("pilgrim_jwt_token") || "";

                if (customer_data.customer.email && customer_data.customer.email != "Not provided") {
                    const encodedEmail = encodeURIComponent(customer_data.customer.email.trim());
                    const checkoutURL = '/checkout?checkout[shipping_address][phone]=8989600025&checkout[shipping_address][phone_country_select]=IN&checkout[email]=' + encodedEmail + '&attributes[pilgrim_jwt_token]=' + pilgrim_token;
                    console.log("checkoutURL: ", checkoutURL);
                    window.location.href = checkoutURL;
                } else {
                    window.location.href = '/checkout';
                }
            } else {
                const checkoutURL = '/checkout?checkout[email]=test123@gmail.com&checkout[shipping_address][phone]=8989600025';
                console.log("custom checkoutURL: ", checkoutURL);
                window.location.href = checkoutURL;
                // window.location.href = '/checkout';
            }
        } catch (error) {
            console.error("[CP]: Issue in passing email in checkout permalink:", error);
            console.error(error);
            // console.log("Something went wrong. Redirecing user without address");
            window.location.href = "/checkout";
        }
    },

    /**
     * Handle checkout button click
     */
    handleCheckoutClick: async function (event) {
        event.preventDefault();
        const checkoutButton = event.currentTarget || event.target;

        if (
            window.PGFreeGiftSync &&
            typeof window.PGFreeGiftSync.waitForCheckoutReady === "function"
        ) {
            await window.PGFreeGiftSync.waitForCheckoutReady(checkoutButton);
        }

        if (checkoutButton.getAttribute('prelogin') == "true") {
            if (this.isLoggedIn()) {
                this.handleCheckoutRedirecting();
                return;
            } else {
                this.showPreloginModal();
                return;
            }
        } else {
            this.handleCheckoutRedirecting();
        }
    },

    /**
     * Send profile email OTP
     */
    sendProfileEmailOTP: function (email) {
        const token = JWTTokenHandler.getStoredToken();
        if (!token) {
            return Promise.reject(new Error('No authentication token found'));
        }

        return fetch(`${this.API_BASE_URL}/send_profile_email_otp/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                email: email
            })
        }).then(response => {
            if (response.status === 401) {
                this.logout();
                throw new Error('Authentication token expired. Please login again.');
            }
            return response.json();
        });
    },

    /**
     * Check whether profile contact changes are available before verification starts
     */
    checkProfileUpdateAvailability: function (profileData) {
        const token = JWTTokenHandler.getStoredToken();
        if (!token) {
            return Promise.reject(new Error('No authentication token found'));
        }

        return fetch(`${this.API_BASE_URL}/check_profile_update_availability/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(profileData)
        }).then(response => {
            if (response.status === 401) {
                this.logout();
                throw new Error('Authentication token expired. Please login again.');
            }
            return response.json();
        });
    },

    /**
     * Verify profile email OTP
     */
    verifyProfileEmailOTP: function (email, otp) {
        const token = JWTTokenHandler.getStoredToken();
        if (!token) {
            return Promise.reject(new Error('No authentication token found'));
        }

        return fetch(`${this.API_BASE_URL}/verify_profile_email_otp/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                email: email,
                otp: otp
            })
        }).then(response => {
            if (response.status === 401) {
                this.logout();
                throw new Error('Authentication token expired. Please login again.');
            }
            return response.json();
        });
    },

    /**
     * Send profile phone OTP
     */
    sendProfilePhoneOTP: function (mobileNumber) {
        const token = JWTTokenHandler.getStoredToken();
        if (!token) {
            return Promise.reject(new Error('No authentication token found'));
        }

        return fetch(`${this.API_BASE_URL}/send_profile_phone_otp/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                mobile_number: mobileNumber
            })
        }).then(response => {
            if (response.status === 401) {
                this.logout();
                throw new Error('Authentication token expired. Please login again.');
            }
            return response.json();
        });
    },

    /**
     * Verify profile phone OTP
     */
    verifyProfilePhoneOTP: function (mobileNumber, otp) {
        const token = JWTTokenHandler.getStoredToken();
        if (!token) {
            return Promise.reject(new Error('No authentication token found'));
        }

        return fetch(`${this.API_BASE_URL}/verify_profile_phone_otp/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                mobile_number: mobileNumber,
                otp: otp
            })
        }).then(response => {
            if (response.status === 401) {
                this.logout();
                throw new Error('Authentication token expired. Please login again.');
            }
            return response.json();
        });
    },
};

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', function () {
    PilgrimAuth.init();
});

// Export for global access
window.PilgrimAuth = PilgrimAuth;
window.JWTTokenHandler = JWTTokenHandler;

// document.addEventListener("click", function (event) {
//     if(event.target.closest(".prelogin-modal")) {
//         document.querySelector("#mini-cart").setAttribute("open", "true");
//     }
// });

// Keep cart drawer open when prelogin modal is visible
document.addEventListener('DOMContentLoaded', () => {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    // Store original removeAttribute method
    const originalRemoveAttribute = cartDrawer.removeAttribute;

    // Override removeAttribute to prevent removing 'open' when prelogin is visible
    cartDrawer.removeAttribute = function (name) {
        if (name === 'open') {
            const preloginModal = document.getElementById('prelogin-modal');
            if (preloginModal?.style.display === 'block') {
                return; // Don't remove 'open' attribute
            }
        }
        return originalRemoveAttribute.call(this, name);
    };
});